import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { Redirect } from 'react-router-dom';
import { InternalLevitateAccountIds } from '../../../models';
import { EnvironmentKey } from '../../../models/AppState';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { AdminPermission, AdminUserSessionContext } from '../../../viewmodels/AdminViewModels';
import { IPrivateRouteProps, PrivateRoute } from '../../../web/components/PrivateRoute';

interface IProps extends IPrivateRouteProps {
	requiredPermissions?: AdminPermission | AdminPermission[];
}

const AdminPrivateRouteSfc: React.FC<IProps> = props => {
	const { redirectPath, location, environment, requiredPermissions } = props;
	const userSession = useUserSession<AdminUserSessionContext>();

	// canAccessSupport is not account-scoped, so it is gated here rather than becoming the one way in
	// that skips the logout eject below. isInternalSupportMailboxAccount is the mailbox-ownership-derived
	// equivalent of InternalLevitateAccountIds, so a support-mailbox-only account (e.g. IT) doesn't need
	// to be hardcoded here too.
	const isInternalAccount =
		!userSession?.account ||
		InternalLevitateAccountIds.has(userSession.account.id) ||
		!!userSession?.isInternalSupportMailboxAccount;
	const hasAdminSurface =
		Object.keys(userSession?.permissions || {}).length > 0 || (!!userSession?.canAccessSupport && isInternalAccount);

	if (
		environment.appType === 'admin' &&
		!!userSession &&
		!userSession.isLoading &&
		!userSession.isLoadingPermissions &&
		!hasAdminSurface
	) {
		if (userSession.account && !isInternalAccount) {
			return <Redirect to={{ pathname: '/logout', state: { from: location } }} />;
		}
		return <Redirect to={{ pathname: redirectPath || '/login', state: { from: location } }} />;
	}
	if (requiredPermissions) {
		// Permissions hydrate async after session load — deciding on an empty map here would
		// redirect hard loads of permission-gated deep links (e.g. /support?ticketId=...) to '/'.
		if (userSession?.isLoading || userSession?.isLoadingPermissions) {
			return null;
		}
		const perms = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];
		if (!perms.every(x => !!userSession.permissions[x])) {
			return <Redirect to={redirectPath || '/'} push={false} />;
		}
	}
	return <PrivateRoute userSession={userSession} {...props} />;
};

const AdminPrivateRouteAsObserver = observer(AdminPrivateRouteSfc);
export const AdminPrivateRoute = inject(EnvironmentKey)(AdminPrivateRouteAsObserver);
